// Banner instalación PWA — captura beforeinstallprompt y ofrece instalar.
// Visible solo en mobile, después de 20s de uso, y solo si no se rechazó antes.
//
// Para iOS Safari (que NO dispara beforeinstallprompt) muestra instrucciones manuales:
// "Compartir → Agregar a inicio".

import { useEffect, useState } from 'react'

const LS_KEY = 'limpack_pwa_install_dismissed'
const LS_KEY_AT = 'limpack_pwa_install_dismissed_at'
const DELAY_MS = 20000 // 20s antes de mostrar
const REPROMPT_DAYS = 30

function isMobile() {
  return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)
}

function isIOSSafari() {
  const ua = navigator.userAgent
  const iOS = /iPad|iPhone|iPod/.test(ua)
  const webkit = /WebKit/.test(ua)
  const notChrome = !/CriOS/.test(ua) && !/FxiOS/.test(ua)
  return iOS && webkit && notChrome
}

function isStandalone() {
  // Ya instalado / agregado al home
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true
}

function fueRechazadoRecientemente() {
  const t = localStorage.getItem(LS_KEY_AT)
  if (!t) return false
  const dias = (Date.now() - Number(t)) / (1000 * 60 * 60 * 24)
  return dias < REPROMPT_DAYS
}

export default function InstallPWAPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [visible, setVisible] = useState(false)
  const [iosManual, setIosManual] = useState(false)

  useEffect(() => {
    // Si ya está instalado o es desktop, no hacer nada
    if (isStandalone() || !isMobile()) return
    if (fueRechazadoRecientemente()) return

    // Chrome/Android: captura el evento
    function onBeforeInstall(e) {
      e.preventDefault()
      setDeferredPrompt(e)
      setTimeout(() => setVisible(true), DELAY_MS)
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall)

    // iOS Safari no dispara beforeinstallprompt — mostrar instrucciones manuales
    if (isIOSSafari()) {
      setTimeout(() => { setIosManual(true); setVisible(true) }, DELAY_MS)
    }

    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall)
  }, [])

  async function instalar() {
    if (!deferredPrompt) return
    try {
      deferredPrompt.prompt()
      const choice = await deferredPrompt.userChoice
      if (choice.outcome === 'accepted') {
        setVisible(false)
      } else {
        dismissTemp()
      }
    } catch (_) { dismissTemp() }
  }

  function dismissTemp() {
    localStorage.setItem(LS_KEY, 'true')
    localStorage.setItem(LS_KEY_AT, String(Date.now()))
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div className="pwa-prompt">
      <div className="pwa-prompt-card">
        <button className="pwa-prompt-close" onClick={dismissTemp} aria-label="Cerrar">×</button>
        <div className="pwa-prompt-icon">
          <img src="/isotipo.png" alt="Limpack" width="48" height="48" />
        </div>
        <div className="pwa-prompt-body">
          <div className="pwa-prompt-title">¿Querés acceso directo?</div>
          {iosManual ? (
            <>
              <p className="pwa-prompt-text">
                Tocá <strong>Compartir</strong> <span style={{fontSize: 14}}>⬆️</span> abajo
                y elegí <strong>"Agregar a inicio"</strong>. Te queda como app nativa, sin instalar nada.
              </p>
              <button className="pwa-prompt-btn pwa-prompt-btn-ghost" onClick={dismissTemp}>Entendido</button>
            </>
          ) : (
            <>
              <p className="pwa-prompt-text">
                Instalá Limpack como app en tu cel. Más rápido, sin perder el ícono entre mil pestañas.
              </p>
              <div className="pwa-prompt-actions">
                <button className="pwa-prompt-btn" onClick={instalar}>Instalar</button>
                <button className="pwa-prompt-btn pwa-prompt-btn-ghost" onClick={dismissTemp}>Ahora no</button>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        .pwa-prompt {
          position: fixed; left: 12px; right: 12px; bottom: 16px;
          z-index: 9000;
          pointer-events: none;
          animation: pwaSlideUp 0.32s ease-out;
        }
        @keyframes pwaSlideUp {
          from { transform: translateY(140%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .pwa-prompt-card {
          position: relative;
          pointer-events: auto;
          max-width: 480px; margin: 0 auto;
          background: #fff; color: #1A3461;
          border-radius: 16px;
          box-shadow: 0 12px 40px rgba(15, 27, 54, 0.25), 0 2px 8px rgba(15, 27, 54, 0.1);
          padding: 16px 18px;
          display: flex; align-items: flex-start; gap: 14px;
          border: 1px solid rgba(0, 168, 181, 0.2);
        }
        .pwa-prompt-icon {
          flex-shrink: 0;
          width: 48px; height: 48px; border-radius: 12px;
          background: #1A3461;
          display: flex; align-items: center; justify-content: center;
          overflow: hidden;
        }
        .pwa-prompt-icon img { width: 38px; height: 38px; object-fit: contain; }
        .pwa-prompt-body { flex: 1; min-width: 0; }
        .pwa-prompt-title {
          font-size: 15px; font-weight: 800; line-height: 1.2; margin-bottom: 4px;
        }
        .pwa-prompt-text {
          font-size: 13px; color: #475569; margin: 0 0 10px;
          line-height: 1.4;
        }
        .pwa-prompt-actions { display: flex; gap: 8px; }
        .pwa-prompt-btn {
          border: 0; background: #00A8B5; color: #fff;
          padding: 8px 14px; border-radius: 8px;
          font-size: 13px; font-weight: 700; cursor: pointer;
          font-family: inherit;
        }
        .pwa-prompt-btn:hover { background: #008a95; }
        .pwa-prompt-btn-ghost {
          background: transparent; color: #64748b;
        }
        .pwa-prompt-btn-ghost:hover { color: #1A3461; background: #F1F5F9; }
        .pwa-prompt-close {
          position: absolute; top: 6px; right: 8px;
          background: transparent; border: 0; cursor: pointer;
          font-size: 22px; line-height: 1; color: #94A3B8;
          padding: 4px 8px;
        }
        .pwa-prompt-close:hover { color: #1A3461; }
      `}</style>
    </div>
  )
}
